import React, { useEffect, useMemo, useState } from "react";
import styles from "./RiskProfileEditModal.module.scss";
import Modal from "../../components/Modal/Modal";
import Select from "../../components/Select/Select";
import Textarea from "../../components/Textarea/Textarea";
import Button from "../../components/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "../../redux/modalStore";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/utils";
import Swal from "sweetalert2";
import { getOrganizationData } from "../../actions/organization";
import { useTranslation } from "react-i18next";

const RISK_LEVEL_OPTIONS = [
  { id: "none", label: "None" },
  { id: "low", label: "Low" },
  { id: "medium", label: "Medium" },
  { id: "high", label: "High" },
];

const RiskProfileEditModal = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const openedModal = useSelector((state) => state.modal.openedModal);
  const { risksData } = useSelector((state) => state.organization);

  const sessionToken = localStorage.getItem("session_token");
  const isOpen = openedModal?.name === "risk_profile_edit_modal";
  const riskNum = openedModal?.data?.riskNum;

  const risk = useMemo(() => {
    if (riskNum === undefined || riskNum === null) return null;
    return risksData?.[riskNum] || null;
  }, [riskNum, risksData]);

  const [level, setLevel] = useState("none");
  const [reason, setReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLevel(String(risk?.level || "none").toLowerCase());
    setReason(risk?.reason || "");
  }, [isOpen, risk]);

  const handleClose = () => {
    if (isSaving) return;
    dispatch(hideModal());
  };

  const handleSave = async () => {
    if (!risk || riskNum === undefined || riskNum === null) return;

    setIsSaving(true);

    const payload = [
      {
        risk_num: riskNum,
        level,
        reason,
      },
    ];

    try {
      await axiosInstance.patch(`/nocodb/risk_profile?org_id=${orgId}`, payload, {
        headers: {
          "session-token": sessionToken,
          "Content-Type": "application/json",
        },
      });

      dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          false,
          false,
          false,
          false,
          false,
        ),
      );

      dispatch(hideModal());
    } catch (err) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: t("riskProfile.saveFailedTitle"),
        text: t("riskProfile.saveFailedText"),
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalTitle = t("riskProfile.editModalTitle");
  const subtitle = risk?.risk_id
    ? `${risk.risk_id} — ${risk?.description || ""}`.trim()
    : risk?.description || "";

  return (
    <Modal
      name="risk_profile_edit_modal"
      title={modalTitle}
      subtitle={subtitle}
      onClose={handleClose}
      width="720px"
    >
      <div className={styles.wrapper}>
        <div className={styles.section}>
          <div className={styles.label}>
            {t("tableAccordion.table.level")}
          </div>
          <Select
            text={t("riskProfile.selectLevel")}
            value={level}
            onChange={(value) => setLevel(value || "none")}
            options={RISK_LEVEL_OPTIONS}
            variant="modal"
          />
        </div>

        <div className={styles.section}>
          <div className={styles.label}>
            {t("tableAccordion.table.reason")}
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("riskProfile.reasonPlaceholder")}
            minHeight="140px"
          />
        </div>

        <div className={styles.actions}>
          <Button
            type="button"
            className={styles.cancelButton}
            onClick={handleClose}
            disabled={isSaving}
            text={t("riskProfile.cancel")}
          />
          <Button
            text={isSaving ? t("riskProfile.saving") : t("riskProfile.save")}
            onClick={handleSave}
            disabled={isSaving}
            className={styles.saveButton}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RiskProfileEditModal;
